import { LocationData, PowerGrid } from '../types';
import { SERVER_TYPES, CHILLER_TYPES, RACK_TYPES, SERVER_MAINTENANCE_RATE, TOR_SWITCH_TYPES, HDA_SWITCH_TYPES, CORE_SWITCH_TYPES, LOCATION_CLIMATE_FACTORS, DEFAULT_CLIMATE_FACTOR, DEMAND_CHARGE_PER_KW_MONTH, WUE_LITRES_PER_KWH, WATER_COST_PER_M3 } from '../constants';

/** Subset of Upgrades that affect operating costs. */
export interface CostUpgradeEffects {
  powerEfficiency?: number;
  coolingEfficiency?: number;
}

// Each level of Power/Cooling Efficiency cuts the relevant draw by 10% (floored at an 80% total cut).
export const powerEfficiencyMultiplier = (upgrades?: CostUpgradeEffects): number =>
  Math.max(0.2, 1 - 0.10 * (upgrades?.powerEfficiency ?? 0));
export const coolingEfficiencyMultiplier = (upgrades?: CostUpgradeEffects): number =>
  Math.max(0.2, 1 - 0.10 * (upgrades?.coolingEfficiency ?? 0));

// Premium racks advertise a power-efficiency bonus (e.g. 1.10 = draws 10% less). Returns the divisor.
const rackPowerBonus = (rackTypeId: string | null | undefined): number => {
  if (!rackTypeId) return 1;
  const rt = RACK_TYPES.find(r => r.id === rackTypeId);
  return rt?.bonuses?.powerEfficiency ?? 1;
};

/** Effective electrical draw (kW) of a single server, after efficiency upgrade + rack bonus. */
const effectiveServerPowerKW = (serverPowerDraw: number, rackTypeId: string | null | undefined, powerMult: number): number =>
  (serverPowerDraw * powerMult) / rackPowerBonus(rackTypeId);

/**
 * Calculates the total power costs for all servers across all locations
 * @param locations All game locations
 * @param powerGrids All available power grids
 * @param currentHour Optional current hour for dynamic pricing
 * @returns Total daily power cost in currency units
 */
export const calculatePowerCosts = (
  locations: Record<string, LocationData>,
  powerGrids: Record<string, PowerGrid>,
  currentHour?: number,
  renewableOffsetKWByLocation?: Record<string, number>,
  upgrades?: CostUpgradeEffects
): number => {
  let totalPowerCost = 0;
  const powerMult = powerEfficiencyMultiplier(upgrades);

  // Loop through all locations
  Object.entries(locations).forEach(([locId, location]) => {
    // Skip if location is not unlocked
    if (!location.unlocked) return;

    // Skip if no power supplier selected
    if (!location.selectedPowerSupplierId || !location.powerGridId) return;

    // Get the selected power supplier
    const grid = powerGrids[location.powerGridId];
    if (!grid) return;

    const supplier = grid.suppliers.find(s => s.id === location.selectedPowerSupplierId);
    if (!supplier) return;

    // Calculate total power used by all servers in this location
    let locationPowerUsage = 0;

    // Loop through all floors and racks
    if (location.floors) {
      Object.values(location.floors).forEach(floor => {
        if (floor.racks) {
          floor.racks.forEach(rack => {
            if (rack.servers) {
              rack.servers.forEach(server => {
                // Server draw, reduced by Power Efficiency upgrade + this rack's efficiency bonus
                const serverType = SERVER_TYPES.find(s => s.id === server.type);
                if (serverType) {
                  locationPowerUsage += effectiveServerPowerKW(serverType.powerDraw, rack.type, powerMult);
                }
              });
            }
          });
        }
      });
    }

    // NOTE: Chiller (cooling) electricity is intentionally NOT added here.
    // It is billed separately, utilisation-scaled, in calculateLocationCoolingCost
    // and surfaced as dailyCoolingCost. Adding it here too would double-bill it.

    // Add power draw from network switches (TOR, HDA, Core)
    if (location.floors) {
      Object.values(location.floors).forEach(floor => {
        // TOR switches (primary + redundant) per rack
        if (floor.racks) {
          floor.racks.forEach(rack => {
            if (rack.torSwitch) {
              const tor = TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitch!.typeId);
              if (tor) locationPowerUsage += tor.powerDrawKW;
            }
            if (rack.torSwitchRedundant) {
              const tor = TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitchRedundant!.typeId);
              if (tor) locationPowerUsage += tor.powerDrawKW;
            }
          });
        }
        // HDA switch per floor
        if (floor.hdaSwitch) {
          const hda = HDA_SWITCH_TYPES.find(t => t.id === floor.hdaSwitch!.typeId);
          if (hda) locationPowerUsage += hda.powerDrawKW;
        }
      });
    }
    // Core switch per location
    if (location.coreSwitch) {
      const core = CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId);
      if (core) locationPowerUsage += core.powerDrawKW;
    }

    // Resolve applicable rate from tariff schedule if provided and hour known
    const resolvedRate = (() => {
      if (supplier.tariffSchedule && currentHour !== undefined) {
        for (const window of supplier.tariffSchedule) {
          const { startHour, endHour, rate } = window;
          if (startHour === endHour) continue; // skip degenerate windows
          if (startHour < endHour) {
            if (currentHour >= startHour && currentHour < endHour) return rate;
          } else {
            // overnight wrap-around
            if (currentHour >= startHour || currentHour < endHour) return rate;
          }
        }
      }
      return supplier.costPerKWh;
    })();

    // Subtract renewable + battery offset — grid only draws what renewables don't cover
    const renewableOffset = renewableOffsetKWByLocation?.[locId] ?? 0;
    const gridDrawKW = Math.max(0, locationPowerUsage - renewableOffset);

    // Compute hourly cost and scale to daily-equivalent
    const energyCost = (gridDrawKW * resolvedRate) * 24;
    // Demand charge: billed on peak grid draw (kW), pro-rated to a daily figure.
    // Renewables that shave the peak (gridDrawKW) reduce this too.
    const demandCost = gridDrawKW * DEMAND_CHARGE_PER_KW_MONTH / 30;
    totalPowerCost += energyCost + demandCost;
  });

  return totalPowerCost;
};

/**
 * Calculates the power cost for a specific location
 * @param location The location to calculate power cost for
 * @param powerGrids All available power grids
 * @param currentHour Optional current hour for dynamic pricing
 * @returns Daily power cost for the location in currency units
 */
export const calculateLocationPowerCost = (
  location: LocationData,
  powerGrids: Record<string, PowerGrid>,
  currentHour?: number,
  upgrades?: CostUpgradeEffects
): number => {
  // Skip if location is not unlocked
  if (!location.unlocked) return 0;

  // Skip if no power supplier selected
  if (!location.selectedPowerSupplierId || !location.powerGridId) return 0;

  // Get the selected power supplier
  const grid = powerGrids[location.powerGridId];
  if (!grid) return 0;

  const supplier = grid.suppliers.find(s => s.id === location.selectedPowerSupplierId);
  if (!supplier) return 0;

  // Calculate total power used by all servers in this location
  let locationPowerUsage = 0;
  const powerMult = powerEfficiencyMultiplier(upgrades);

  // Loop through all floors and racks
  if (location.floors) {
    Object.values(location.floors).forEach(floor => {
      if (floor.racks) {
        floor.racks.forEach(rack => {
          if (rack.servers) {
            rack.servers.forEach(server => {
              // Server draw, reduced by Power Efficiency upgrade + this rack's efficiency bonus
              const serverType = SERVER_TYPES.find(s => s.id === server.type);
              if (serverType) {
                locationPowerUsage += effectiveServerPowerKW(serverType.powerDraw, rack.type, powerMult);
              }
            });
          }
        });
      }
    });
  }

  // Chiller electricity is billed separately in calculateLocationCoolingCost — not here (avoids double-billing).

  // Add power draw from network switches (TOR, HDA, Core)
  if (location.floors) {
    Object.values(location.floors).forEach(floor => {
      if (floor.racks) {
        floor.racks.forEach(rack => {
          if (rack.torSwitch) {
            const tor = TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitch!.typeId);
            if (tor) locationPowerUsage += tor.powerDrawKW;
          }
          if (rack.torSwitchRedundant) {
            const tor = TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitchRedundant!.typeId);
            if (tor) locationPowerUsage += tor.powerDrawKW;
          }
        });
      }
      if (floor.hdaSwitch) {
        const hda = HDA_SWITCH_TYPES.find(t => t.id === floor.hdaSwitch!.typeId);
        if (hda) locationPowerUsage += hda.powerDrawKW;
      }
    });
  }
  if (location.coreSwitch) {
    const core = CORE_SWITCH_TYPES.find(t => t.id === location.coreSwitch!.typeId);
    if (core) locationPowerUsage += core.powerDrawKW;
  }

  // Resolve applicable rate similar to global calculation
  const resolvedRate = (() => {
    if (supplier.tariffSchedule && currentHour !== undefined) {
      for (const window of supplier.tariffSchedule) {
        const { startHour, endHour, rate } = window;
        if (startHour === endHour) continue;
        if (startHour < endHour) {
          if (currentHour >= startHour && currentHour < endHour) return rate;
        } else {
          if (currentHour >= startHour || currentHour < endHour) return rate;
        }
      }
    }
    return supplier.costPerKWh;
  })();

  // Calculate cost using resolved rate and scale to daily, plus the peak-demand charge.
  const energyCost = (locationPowerUsage * resolvedRate) * 24;
  const demandCost = locationPowerUsage * DEMAND_CHARGE_PER_KW_MONTH / 30;
  return energyCost + demandCost;
};

/**
 * Total server IT power draw (kW) for a location — a good proxy for heat generated.
 * Shared by the cooling-cost calc and the game loop's renewable accounting.
 */
export const getLocationServerPowerKW = (location: LocationData): number => {
  let kw = 0;
  if (location.floors) {
    Object.values(location.floors).forEach(floor => {
      floor.racks?.forEach(rack => {
        rack.servers?.forEach(server => {
          const st = SERVER_TYPES.find(s => s.id === server.type);
          if (st) kw += st.powerDraw;
        });
      });
    });
  }
  return kw;
};

/**
 * Utilisation-scaled electricity draw (kW) of the whole cooling chain for a location:
 * chillers + heat rejection (climate-adjusted) + CRAH air handlers.
 * Single source of truth so cost and renewable-offset logic agree.
 */
export const getLocationCoolingPowerKW = (location: LocationData, locationId?: string): number => {
  const hasChiller = (location.chillers?.length ?? 0) > 0;
  const hasHeatRej = !!location.heatRejection?.installed;
  const hasCrah = !!location.crah?.installed;
  if (!hasChiller && !hasHeatRej && !hasCrah) return 0;

  const climate = (locationId && LOCATION_CLIMATE_FACTORS[locationId]) || DEFAULT_CLIMATE_FACTOR;
  const serverHeat = getLocationServerPowerKW(location);

  // Effective delivered cooling capacity (min of the three stages)
  let chillerCap = 0;
  let chillerNameplatePower = 0;
  location.chillers?.forEach(chiller => {
    const type = CHILLER_TYPES.find(t => t.id === chiller.typeId);
    const level = type?.levels.find(l => l.level === chiller.level);
    if (level) { chillerCap += level.coolingOutputKW * level.efficiency; chillerNameplatePower += level.powerConsumptionKW; }
  });
  const heatRejCap = hasHeatRej ? location.heatRejection!.capacityKW * climate : 0;
  const crahCap = hasCrah ? location.crah!.capacityKW : 0;
  const effectiveCap = Math.min(
    hasHeatRej ? heatRejCap : 0,
    hasChiller ? chillerCap : 0,
    hasCrah ? crahCap : 0,
  );

  // Single utilisation factor across the chain (idle floor 0.1)
  const utilization = effectiveCap > 0 ? Math.min(serverHeat / effectiveCap, 1) : 0.1;

  const chillerPower = chillerNameplatePower * utilization;
  // Free cooling: colder climates need less heat-rejection power
  const heatRejPower = hasHeatRej ? (location.heatRejection!.powerDrawKW / climate) * utilization : 0;
  const crahPower = hasCrah ? location.crah!.powerDrawKW * utilization : 0;

  return chillerPower + heatRejPower + crahPower;
};

/**
 * Daily water cost (WUE) for a location's evaporative cooling towers.
 * Water is only consumed once a heat-rejection stage is installed. Litres scale
 * with the IT heat rejected; hot/dry climates evaporate more (climate factor <1
 * → higher usage, colder climates <-> lower). Returns $/day.
 */
export const getLocationWaterCost = (location: LocationData, locationId?: string): number => {
  if (!location.heatRejection?.installed) return 0;
  const serverHeatKW = getLocationServerPowerKW(location);
  if (serverHeatKW <= 0) return 0;
  const climate = (locationId && LOCATION_CLIMATE_FACTORS[locationId]) || DEFAULT_CLIMATE_FACTOR;
  // kWh of heat rejected per day, ÷ climate so warmer sites (factor <1) use more water
  const litresPerDay = (serverHeatKW * 24) * (WUE_LITRES_PER_KWH / climate);
  return (litresPerDay / 1000) * WATER_COST_PER_M3;
};

/**
 * Calculates the total cooling costs for all servers across all locations.
 * @param renewableCoolingOffsetKWByLocation kW of renewable/battery power
 *   left over after covering IT load — reduces the cooling grid draw.
 */
export const calculateCoolingCosts = (
  locations: Record<string, LocationData>,
  powerGrids?: Record<string, any>,
  renewableCoolingOffsetKWByLocation?: Record<string, number>,
  upgrades?: CostUpgradeEffects
): number => {
  let totalCoolingCost = 0;
  Object.entries(locations).forEach(([locId, location]) => {
    if (!location.unlocked) return;
    totalCoolingCost += calculateLocationCoolingCost(
      location, powerGrids, renewableCoolingOffsetKWByLocation?.[locId] ?? 0, upgrades, locId
    );
  });
  return totalCoolingCost;
};

/**
 * Calculates the cooling cost for a specific location.
 * @param renewableCoolingOffsetKW renewable/battery power applied to the cooling draw
 * @returns Daily cooling cost for the location in currency units
 */
export const calculateLocationCoolingCost = (
  location: LocationData,
  powerGrids?: Record<string, any>,
  renewableCoolingOffsetKW: number = 0,
  upgrades?: CostUpgradeEffects,
  locationId?: string
): number => {
  if (!location.unlocked) return 0;

  // Resolve supplier rate — fall back to $0.15 if grid/supplier not available
  let costPerKWh = 0.15;
  if (powerGrids && location.powerGridId && location.selectedPowerSupplierId) {
    const grid = powerGrids[location.powerGridId];
    const supplier = grid?.suppliers?.find((s: any) => s.id === location.selectedPowerSupplierId);
    if (supplier?.costPerKWh) costPerKWh = supplier.costPerKWh;
  }

  // Cooling Efficiency upgrade reduces the cooling draw
  const totalCoolingPower = getLocationCoolingPowerKW(location, locationId) * coolingEfficiencyMultiplier(upgrades);
  // Renewables/battery offset the cooling grid draw too (floored at 0)
  const gridCoolingPower = Math.max(0, totalCoolingPower - renewableCoolingOffsetKW);

  const electricityCost = gridCoolingPower * costPerKWh * 24;
  // Evaporative cooling towers also consume water (WUE) — billed on top.
  const waterCost = getLocationWaterCost(location, locationId);
  return electricityCost + waterCost;
};

/**
 * Calculates total daily maintenance costs for all installed servers across all locations.
 * Rate: SERVER_MAINTENANCE_RATE per month → divided by 30 for daily cost.
 */
export const calculateMaintenanceCosts = (locations: Record<string, LocationData>): number => {
  let total = 0;
  Object.values(locations).forEach(location => {
    if (!location.unlocked) return;
    if (!location.floors) return;
    Object.values(location.floors).forEach(floor => {
      floor.racks?.forEach(rack => {
        rack.servers?.forEach(server => {
          const st = SERVER_TYPES.find(s => s.id === server.type);
          if (st) total += (st.cost * SERVER_MAINTENANCE_RATE) / 30;
        });
      });
    });
  });
  return total;
};
